import React, { useState } from 'react';
import { appToast } from '../../../../components/AppToast';
import { biometricsApi } from '../api/biometricsApi';

export default function BiometricsUploadModal({
  isOpen,
  onClose,
  onUploadSuccess,
}) {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      await biometricsApi.downloadTemplate();
      appToast.success('Template downloaded successfully.');
    } catch (err) {
      console.error(err);
      appToast.error('Failed to download template.');
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      appToast.error('Please select an Excel file (.xlsx) to upload.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      appToast.error('Please upload a valid Excel file (.xlsx).');
      return;
    }

    setIsUploading(true);
    setResult(null);
    try {
      const res = await biometricsApi.uploadBiometricFile(file);
      setResult(res);
      if (res.imported_count > 0) {
        appToast.success(res.message || `Successfully imported ${res.imported_count} swipe record(s).`);
        onUploadSuccess?.();
      } else if (res.failed_count > 0) {
        appToast.warning(`Import completed with errors. ${res.failed_count} row(s) failed.`);
      } else {
        appToast.info('No records were imported.');
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || 'Failed to upload file. Please check the file format.';
      appToast.error(msg);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 p-4 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="w-full max-w-lg rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/15 pb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-xl">cloud_upload</span>
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-on-surface">Upload Biometric Swipes</h3>
              <p className="text-xs text-on-surface-variant">
                Import swipe records via Excel (.xlsx)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface transition"
          >
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>

        <div className="my-5 space-y-4">
          {/* Template Download */}
          <div className="flex items-center justify-between rounded-xl bg-surface-container-low p-3">
            <div className="flex items-start gap-2">
              <span className="material-symbols-outlined text-base text-primary shrink-0 mt-0.5">description</span>
              <div>
                <p className="text-xs font-bold text-on-surface">Biometric Swipe Template</p>
                <p className="text-[11px] text-on-surface-variant">
                  Columns: GrNo, Name, Date, PunchTime, DeviceID, Remark
                </p>
              </div>
            </div>
            <button
              type="button"
              disabled={isDownloadingTemplate}
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-[11px] font-bold text-on-primary hover:bg-primary/90 transition disabled:opacity-60 whitespace-nowrap"
            >
              <span className="material-symbols-outlined text-sm">download</span>
              {isDownloadingTemplate ? 'Downloading...' : 'Download'}
            </button>
          </div>

          {/* File Upload Area */}
          <div className="rounded-2xl border-2 border-dashed border-outline-variant/40 bg-surface-container-low/40 p-6 text-center hover:border-primary/50 transition">
            <input
              type="file"
              id="biometric-file-input"
              accept=".xlsx"
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="biometric-file-input"
              className="flex flex-col items-center justify-center gap-2 cursor-pointer"
            >
              <span className="material-symbols-outlined text-4xl text-primary/70">upload_file</span>
              <span className="text-xs font-bold text-on-surface">
                {file ? file.name : 'Click to select Excel file (.xlsx)'}
              </span>
              <span className="text-[11px] text-on-surface-variant">
                Template columns: GrNo, Name, Date (YYYY-MM-DD), PunchTime (HH:MM), DeviceID, Remark
              </span>
            </label>
          </div>

          {/* Upload Result */}
          {result && (
            <div className={`rounded-xl p-3 text-xs space-y-1 ${result.imported_count > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
              <p className="font-bold text-on-surface">
                {result.message}
              </p>
              <p className="text-on-surface-variant">
                Total rows: {result.total_rows} &nbsp;|&nbsp;
                Imported: <span className="text-emerald-700 font-bold">{result.imported_count}</span> &nbsp;|&nbsp;
                Failed: <span className="text-error font-bold">{result.failed_count}</span>
              </p>
              {result.errors && result.errors.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-error text-[11px] max-h-24 overflow-y-auto">
                  {result.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>• {e}</li>
                  ))}
                  {result.errors.length > 10 && (
                    <li className="font-bold">...and {result.errors.length - 10} more errors.</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 pt-4">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-xl bg-secondary-container px-5 py-2 text-xs font-bold text-on-secondary-container transition hover:brightness-95"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              type="button"
              disabled={isUploading || !file}
              onClick={handleUpload}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-container px-6 py-2 text-xs font-bold text-on-primary shadow-md shadow-primary/20 transition hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-base">cloud_upload</span>
              <span>{isUploading ? 'Importing...' : 'Upload Swipes'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
